import React, { useCallback, useEffect, useMemo } from 'react';

import { AppOnboarding } from '@vyce/core/src/components/AppOnboarding';
import { OnboardingStep } from '@vyce/core/src/types';

import { Greetings } from '@vyce/core/src/components/onboarding';

import { ScheduleType } from '@vyce/core/src/modules/timeModule/constants';

import {
  AddAnotherShiftStep,
  BreaksStep,
  DefaultRateStep,
  DoYouNeedSpecificLocationStep,
  LocationAreaStep,
  LocationIsSetupDialog,
  LocationNameStep,
  OvertimeScheduleStep,
  PostcodeStep,
  ScheduleHoursStep,
  ShiftStep,
} from './components';
import { TrackingOptionsStep } from './components/TrackingOptionsStep';
import { useData } from './hooks/useData';
import { AddLocationManagersStep } from './components/AddLocationManagersStep';
import { buildShiftFieldName, buildShiftStepName, parseStepName } from './helpers/shiftStepName';
import { SHIFT_STEP } from './constants';
import { Props } from './types';

export const LocationOnboarding = ({ selectedCompany, fetchCompanyData, user, getStaffRequest }: Props) => {
  const initialStep = useMemo(
    () => ({
      component: <Greetings subtitle="Let’s have you set up in minutes." firstName={user.first_name} />,
      name: 'Greetings',
    }),
    [user.first_name]
  );

  const {
    activeStep,
    withLocation,
    disableNext,
    loading,
    methods,
    steps,
    childRef,
    open,
    newLocationId,
    staff,
    shiftsCount,
    shiftsWithOvertime,
    shiftsWithOvertimeKey,
    shiftAnswers,
    pendingShiftIndex,
    setPendingShiftIndex,
    addAnotherShift,
    skipAnotherShift,
    clear,
    setDefaultSteps,
    setSteps,
    setOpen,
    handleNext,
    setActiveStep,
    scrollDown,
    handleNoClick,
    handleYesClick,
    setStaffSubstring,
  } = useData({ selectedCompany, fetchCompanyData, user, initialStep, getStaffRequest });

  const buildAddAnotherShiftStep = useCallback(
    (shiftIndex: number): OnboardingStep => ({
      component: (
        <AddAnotherShiftStep
          answered={shiftAnswers[shiftIndex] !== undefined}
          addedAnother={!!shiftAnswers[shiftIndex]}
          onAddAnother={() => addAnotherShift(shiftIndex)}
          onSkip={() => skipAnotherShift(shiftIndex)}
        />
      ),
      name: buildShiftStepName(SHIFT_STEP.addAnother, shiftIndex),
    }),
    [shiftAnswers, addAnotherShift, skipAnotherShift]
  );

  /**
   * One shift is one block of steps. The block is repeated per shift, so every step in it is
   * bound to `shifts.<index>` and named with that index — see `buildShiftStepName`.
   */
  const buildShiftSteps = useCallback(
    (shiftIndex: number): OnboardingStep[] => {
      const shiftFieldName = buildShiftFieldName(shiftIndex);

      return [
        {
          component: <ShiftStep shiftFieldName={shiftFieldName} shiftIndex={shiftIndex} />,
          name: buildShiftStepName(SHIFT_STEP.shift, shiftIndex),
        },
        {
          component: (
            <DefaultRateStep
              type={ScheduleType.REGULAR}
              shiftFieldName={shiftFieldName}
              shiftIndex={shiftIndex}
            />
          ),
          name: buildShiftStepName(SHIFT_STEP.regularRate, shiftIndex),
        },
        {
          component: (
            <ScheduleHoursStep
              type={ScheduleType.REGULAR}
              shiftFieldName={shiftFieldName}
              shiftIndex={shiftIndex}
            />
          ),
          name: buildShiftStepName(SHIFT_STEP.regularSchedule, shiftIndex),
        },
        {
          component: <OvertimeScheduleStep shiftFieldName={shiftFieldName} shiftIndex={shiftIndex} />,
          name: buildShiftStepName(SHIFT_STEP.overtime, shiftIndex),
        },
        {
          component: (
            <BreaksStep scrollDown={scrollDown} shiftFieldName={shiftFieldName} shiftIndex={shiftIndex} />
          ),
          name: buildShiftStepName(SHIFT_STEP.breaks, shiftIndex),
        },
        buildAddAnotherShiftStep(shiftIndex),
      ];
    },
    [buildAddAnotherShiftStep]
  );

  const defaultSteps: OnboardingStep[] = useMemo(
    () => [
      initialStep,
      {
        component: (
          <DoYouNeedSpecificLocationStep
            withLocation={withLocation}
            handleYesClick={handleYesClick}
            handleNoClick={handleNoClick}
            activeStep={activeStep}
          />
        ),
        name: 'DoYouNeedSpecificLocationStep',
      },
      {
        component: <TrackingOptionsStep />,
        name: 'TrackingOption',
      },
      {
        component: <PostcodeStep />,
        name: 'PostcodeStep',
      },
      {
        component: <LocationAreaStep />,
        name: 'LocationAreaStep',
      },
      ...buildShiftSteps(0),
      {
        component: <AddLocationManagersStep setSubstring={setStaffSubstring} staff={staff} />,
        name: 'AddLocationManagersStep',
      },

      // {
      //   component: <ApproverStep title="Now define who approves your team’s hours for this location." />,
      //   name: 'ApproverStep',
      // },
      // {
      //   component: <AllocateToPayScheduleStep />,
      //   name: 'AllocateToPayScheduleStep',
      // },
      {
        component: <LocationNameStep />,
        name: 'LocationNameStep',
      },
    ],
    [steps]
  );

  // update activeStep props
  useEffect(() => {
    setSteps(steps =>
      steps.map(item => {
        if (item.name === 'DoYouNeedSpecificLocationStep') {
          return {
            component: (
              <DoYouNeedSpecificLocationStep
                withLocation={withLocation}
                handleYesClick={handleYesClick}
                handleNoClick={handleNoClick}
                activeStep={activeStep}
              />
            ),
            name: 'DoYouNeedSpecificLocationStep',
          };
        }
        if (item.name === 'AddLocationManagersStep') {
          return {
            component: <AddLocationManagersStep setSubstring={setStaffSubstring} staff={staff} />,
            name: 'AddLocationManagersStep',
          };
        }
        const { step, shiftIndex } = parseStepName(item.name);
        if (step === SHIFT_STEP.addAnother && shiftIndex !== undefined) {
          return buildAddAnotherShiftStep(shiftIndex);
        }

        return item;
      })
    );
  }, [activeStep, withLocation, setStaffSubstring, staff, buildAddAnotherShiftStep]);

  // Give every shift the user has added its own block of steps, right after the
  // "add another shift?" step that created it.
  useEffect(() => {
    // Functional update: this runs in the same commit as the step-props refresh above, and
    // rebuilding from the render's `steps` would throw that refresh away.
    setSteps(currentSteps => {
      let newSteps = currentSteps;

      for (let shiftIndex = 1; shiftIndex < shiftsCount; shiftIndex++) {
        if (newSteps.some(step => step.name === buildShiftStepName(SHIFT_STEP.shift, shiftIndex))) {
          continue;
        }
        const previousStepIndex = newSteps.findIndex(
          step => step.name === buildShiftStepName(SHIFT_STEP.addAnother, shiftIndex - 1)
        );
        if (previousStepIndex === -1) {
          continue;
        }
        newSteps = [...newSteps];
        newSteps.splice(previousStepIndex + 1, 0, ...buildShiftSteps(shiftIndex));
      }

      return newSteps;
    });
  }, [shiftsCount, steps, buildShiftSteps]);

  // Setting both overtime times unlocks two more steps for that shift.
  useEffect(() => {
    setSteps(currentSteps => {
      let newSteps = currentSteps;

      shiftsWithOvertime.forEach(shiftIndex => {
        if (
          newSteps.some(step => step.name === buildShiftStepName(SHIFT_STEP.overtimeSchedule, shiftIndex))
        ) {
          return;
        }
        const overtimeStepIndex = newSteps.findIndex(
          step => step.name === buildShiftStepName(SHIFT_STEP.overtime, shiftIndex)
        );
        if (overtimeStepIndex === -1) {
          return;
        }
        const shiftFieldName = buildShiftFieldName(shiftIndex);
        newSteps = [...newSteps];
        newSteps.splice(
          overtimeStepIndex + 1,
          0,
          {
            component: (
              <DefaultRateStep
                type={ScheduleType.OVERTIME}
                shiftFieldName={shiftFieldName}
                shiftIndex={shiftIndex}
              />
            ),
            name: buildShiftStepName(SHIFT_STEP.overtimeRate, shiftIndex),
          },
          {
            component: (
              <ScheduleHoursStep
                type={ScheduleType.OVERTIME}
                shiftFieldName={shiftFieldName}
                shiftIndex={shiftIndex}
              />
            ),
            name: buildShiftStepName(SHIFT_STEP.overtimeSchedule, shiftIndex),
          }
        );
      });

      return newSteps;
    });
  }, [shiftsWithOvertimeKey, steps]);

  // Once the new shift's steps are in place, move the wizard onto the first of them.
  useEffect(() => {
    if (pendingShiftIndex === undefined) {
      return;
    }
    const stepIndex = steps.findIndex(
      step => step.name === buildShiftStepName(SHIFT_STEP.shift, pendingShiftIndex)
    );
    if (stepIndex === -1) {
      return;
    }
    setActiveStep({ step: steps[stepIndex], number: stepIndex });
    setPendingShiftIndex(undefined);
    setTimeout(scrollDown, 400);
  }, [pendingShiftIndex, steps]);

  useEffect(() => {
    setSteps(defaultSteps);
  }, []);

  useEffect(() => {
    setDefaultSteps(defaultSteps);
  }, [defaultSteps]);

  return (
    <>
      <AppOnboarding
        disableNext={disableNext}
        activeStep={activeStep}
        currentMessage=""
        setActiveStep={setActiveStep}
        methods={methods}
        handleNext={handleNext}
        steps={steps}
        loading={loading}
        ref={childRef}
      />

      <LocationIsSetupDialog
        setOpen={setOpen}
        open={open}
        name={methods.getValues('name')}
        locationId={newLocationId}
        createNewLocation={clear}
      />
    </>
  );
};
